import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { useEffect } from 'react'

import { hasCode } from '@/api/errors'
import { companyInsightsService } from '@/features/company/api/insightsService'
import { companyKeys } from '@/features/company/api/keys'
import { companyProfileService } from '@/features/company/api/profileService'
import type { FloorsheetPeriod } from '@/features/company/lib/floorsheetPeriod'
import type { CompanyFloorsheetSummary } from '@/schemas/company-floorsheet'
import type { CompanyInsight, InsightPage } from '@/schemas/company-insights'
import {
  PANEL_NOT_AVAILABLE_FOR_SECTOR,
  type AgmPage,
  type AnnouncementPage,
  type BoardMember,
  type CompanyComparison,
  type CompanyDividends,
  type CompanyEvents,
  type CompanyFinancialBlock,
  type CompanyIndices,
  type CompanyKeyNumbers,
  type CompanyMetrics,
  type CompanyPanel,
  type CompanyReturns,
  type CompanySentiment,
  type CompanyShareholding,
  type CompanySwot,
  type CompanyTechnicals,
  type CompanyVolatility,
  type FinancialBlock,
  type MetricsQuery,
  type PanelNotAvailable,
  type PanelQuery,
  type PanelTab,
  type ReportLink,
} from '@/schemas/company-profile'

/**
 * The hooks the Stock page's tabs call — one `queryOptions` factory per read
 * (what a route loader hands `prefetchRouteData`), and hooks that spread it.
 *
 * Every tab owns its read and its skeleton (docs/route-loading.md, "Main vs
 * secondary"): the overview is the page's MAIN read and these are all
 * SECONDARY, so they are plain `useQuery` hooks, never suspense. A read keyed
 * on something the member changes — a period, a page — takes
 * `keepPreviousData`, so a change dims the old figures rather than blanking
 * them.
 *
 * NOTHING HERE POLLS. Four freshness tiers, on the factory rather than the
 * hook because `prefetchRouteData` honours the query's OWN staleTime:
 */

/**
 * Price-bearing reads. The comparison prints each company's last price and
 * today's move, which mm-be refreshes on a 60s cron — the app-wide 30s the
 * overview (and the Market pages) already use.
 */
const QUOTE_STALE_MS = 30_000

/**
 * Readings mm-be's legacy crons compute once a session — sentiment and the
 * technical indicators. Five minutes: the same window the app gives a
 * day-level series (`api/endpointCache.ts` `DAY_LEVEL_POLL_MS`), since the
 * figure cannot move inside a session.
 */
const READING_STALE_MS = 5 * 60_000

/**
 * Notices. Posted through the day on no schedule, so a revisit within ten
 * minutes re-reads the cache and one after it re-asks.
 */
const NOTICE_STALE_MS = 10 * 60_000

/**
 * Filings (metrics, Advanced panels, Financials, Key numbers) and corporate actions
 * (dividends, events, AGMs, the board, shareholding, SWOT, reports). They
 * change a few times a year, and mm-be already caches a populated filing for
 * far longer than this; half an hour keeps a long session from re-asking the
 * data service, whose 120-requests-a-minute budget all of Money Mitra shares.
 */
const RECORD_STALE_MS = 30 * 60_000

// ── Readings ─────────────────────────────────────────────────────────────────

/** Overview's greed meter and the rail's fear-and-greed. `null`: no reading recorded. */
export const companySentimentQueryOptions = (symbol: string) =>
  queryOptions<CompanySentiment | null>({
    queryKey: companyKeys.sentiment(symbol),
    queryFn: () => companyProfileService.sentiment(symbol),
    staleTime: READING_STALE_MS,
  })

export const useCompanySentiment = (symbol: string): UseQueryResult<CompanySentiment | null> =>
  useQuery(companySentimentQueryOptions(symbol))

/**
 * Technical's figures and Advanced · Trading's (owner decision 2: both read
 * this one endpoint, so they cannot disagree). `null`: never computed.
 */
export const companyTechnicalsQueryOptions = (symbol: string) =>
  queryOptions<CompanyTechnicals | null>({
    queryKey: companyKeys.technicals(symbol),
    queryFn: () => companyProfileService.technicals(symbol),
    staleTime: READING_STALE_MS,
  })

export const useCompanyTechnicals = (symbol: string): UseQueryResult<CompanyTechnicals | null> =>
  useQuery(companyTechnicalsQueryOptions(symbol))

/**
 * Floorsheet's summary and broker activity for one window. The reading tier
 * because mm-be caches each (symbol, period) for five minutes: asking sooner
 * only returns the same answer.
 */
export const companyFloorsheetSummaryQueryOptions = (
  symbol: string,
  period: FloorsheetPeriod,
  brokers: number,
) =>
  queryOptions<CompanyFloorsheetSummary>({
    queryKey: companyKeys.floorsheetSummary(symbol, period, brokers),
    queryFn: () => companyProfileService.floorsheet(symbol, period, brokers),
    staleTime: READING_STALE_MS,
  })

/** `keepPreviousData`: a period change keeps the last window's figures up until the next lands. */
export const useCompanyFloorsheetSummary = (
  symbol: string,
  period: FloorsheetPeriod,
  brokers: number,
): UseQueryResult<CompanyFloorsheetSummary> =>
  useQuery({
    ...companyFloorsheetSummaryQueryOptions(symbol, period, brokers),
    placeholderData: keepPreviousData,
  })

// ── Filings (the data service) ───────────────────────────────────────────────
//
// Metrics, panels, blocks, key numbers (and compare, below) go through the external
// data service. When it cannot answer, the query ERRORS with a `DATA_FEED_*`
// 503 — read it with `filedFiguresFailure(query.error)` (`./errors.ts`) and
// draw the calm "couldn't load filed figures" state with a retry.

/** The computed metrics at one period, or the latest. */
export const companyMetricsQueryOptions = (symbol: string, query: MetricsQuery = {}) =>
  queryOptions<CompanyMetrics>({
    queryKey: companyKeys.metrics(symbol, query),
    queryFn: () => companyProfileService.metrics(symbol, query),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyMetrics = (
  symbol: string,
  query: MetricsQuery = {},
): UseQueryResult<CompanyMetrics> =>
  useQuery({ ...companyMetricsQueryOptions(symbol, query), placeholderData: keepPreviousData })

/**
 * One Advanced tab, or `'not-available-for-sector'`.
 *
 * `404 PANEL_NOT_AVAILABLE_FOR_SECTOR` is a VALUE here (docs/route-loading.md,
 * "Not-found answers"): no feed is mapped for this sector and tab, retrying
 * cannot change that, and the tab draws its own "not available for {sector}"
 * state rather than an error. Every other failure still throws — including
 * the data service's 503s, which stay errors so they are retried and never
 * cached (see `filedFiguresFailure` for why and how to draw them).
 */
export const companyPanelQueryOptions = (symbol: string, tab: PanelTab, query: PanelQuery = {}) =>
  queryOptions<CompanyPanel | PanelNotAvailable>({
    queryKey: companyKeys.panel(symbol, tab, query),
    queryFn: async () => {
      try {
        return await companyProfileService.panel(symbol, tab, query)
      } catch (error) {
        if (hasCode(error, 'PANEL_NOT_AVAILABLE_FOR_SECTOR')) return PANEL_NOT_AVAILABLE_FOR_SECTOR
        throw error
      }
    },
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyPanel = (
  symbol: string,
  tab: PanelTab,
  query: PanelQuery = {},
): UseQueryResult<CompanyPanel | PanelNotAvailable> =>
  useQuery({ ...companyPanelQueryOptions(symbol, tab, query), placeholderData: keepPreviousData })

/**
 * One Financials block (`/financials/{block}/`), or `'not-available-for-sector'`
 * — the panel's 404 as a value, for the same reason (a sector with no feed for
 * the block; several have no cash-flow feed). The query is normalised so one
 * period is one key: no `undefined` members.
 */
export const companyFinancialBlockQueryOptions = (
  symbol: string,
  block: FinancialBlock,
  query: PanelQuery = {},
) => {
  const normalised: PanelQuery = {
    ...(query.year === undefined ? {} : { year: query.year }),
    ...(query.period === undefined ? {} : { period: query.period }),
  }
  return queryOptions<CompanyFinancialBlock | PanelNotAvailable>({
    queryKey: companyKeys.financialBlock(symbol, block, normalised),
    queryFn: async () => {
      try {
        return await companyProfileService.financialBlock(symbol, block, normalised)
      } catch (error) {
        if (hasCode(error, 'PANEL_NOT_AVAILABLE_FOR_SECTOR')) return PANEL_NOT_AVAILABLE_FOR_SECTOR
        throw error
      }
    },
    staleTime: RECORD_STALE_MS,
  })
}

export const useCompanyFinancialBlock = (
  symbol: string,
  block: FinancialBlock,
  query: PanelQuery = {},
): UseQueryResult<CompanyFinancialBlock | PanelNotAvailable> =>
  useQuery({
    ...companyFinancialBlockQueryOptions(symbol, block, query),
    placeholderData: keepPreviousData,
  })

/**
 * Overview's Key numbers (`/key-numbers/`), or `'not-available-for-sector'`
 * when there is no card to draw: the block's 404 as a value, and
 * `COMPANY_NOT_FOUND` with it — the page's own overview read already says an
 * unknown ticker, so this card only has to stay away. A 503 still throws, as
 * every filed-figures outage does (`filedFiguresFailure`).
 */
export const companyKeyNumbersQueryOptions = (symbol: string) =>
  queryOptions<CompanyKeyNumbers | PanelNotAvailable>({
    queryKey: companyKeys.keyNumbers(symbol),
    queryFn: async () => {
      try {
        return await companyProfileService.keyNumbers(symbol)
      } catch (error) {
        if (
          hasCode(error, 'PANEL_NOT_AVAILABLE_FOR_SECTOR') ||
          hasCode(error, 'COMPANY_NOT_FOUND')
        ) {
          return PANEL_NOT_AVAILABLE_FOR_SECTOR
        }
        throw error
      }
    },
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyKeyNumbers = (
  symbol: string,
): UseQueryResult<CompanyKeyNumbers | PanelNotAvailable> =>
  useQuery(companyKeyNumbersQueryOptions(symbol))

/**
 * Price returns beside the sector index and NEPSE (`/returns/`). Every window
 * ends at the last completed session, so a figure cannot move inside one: the
 * readings' five minutes.
 */
export const companyReturnsQueryOptions = (symbol: string) =>
  queryOptions<CompanyReturns>({
    queryKey: companyKeys.returns(symbol),
    queryFn: () => companyProfileService.returns(symbol),
    staleTime: READING_STALE_MS,
  })

export const useCompanyReturns = (symbol: string): UseQueryResult<CompanyReturns> =>
  useQuery(companyReturnsQueryOptions(symbol))

/**
 * The indices a company is in and its weight in each (`/indices/`). Built from the session's
 * closing figures, so it cannot move inside one: the readings' five minutes.
 */
export const companyIndicesQueryOptions = (symbol: string) =>
  queryOptions<CompanyIndices>({
    queryKey: companyKeys.indices(symbol),
    queryFn: () => companyProfileService.indices(symbol),
    staleTime: READING_STALE_MS,
  })

export const useCompanyIndices = (symbol: string): UseQueryResult<CompanyIndices> =>
  useQuery(companyIndicesQueryOptions(symbol))

/** Daily volatility against NEPSE's (`/volatility/`), over completed sessions: the readings' tier. */
export const companyVolatilityQueryOptions = (symbol: string) =>
  queryOptions<CompanyVolatility>({
    queryKey: companyKeys.volatility(symbol),
    queryFn: () => companyProfileService.volatility(symbol),
    staleTime: READING_STALE_MS,
  })

export const useCompanyVolatility = (symbol: string): UseQueryResult<CompanyVolatility> =>
  useQuery(companyVolatilityQueryOptions(symbol))

// ── Corporate actions ────────────────────────────────────────────────────────

/** The dividend history and its stat line. */
export const companyDividendsQueryOptions = (symbol: string) =>
  queryOptions<CompanyDividends>({
    queryKey: companyKeys.dividends(symbol),
    queryFn: () => companyProfileService.dividends(symbol),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyDividends = (symbol: string): UseQueryResult<CompanyDividends> =>
  useQuery(companyDividendsQueryOptions(symbol))

/** The corporate-action calendar. */
export const companyEventsQueryOptions = (symbol: string) =>
  queryOptions<CompanyEvents>({
    queryKey: companyKeys.events(symbol),
    queryFn: () => companyProfileService.events(symbol),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyEvents = (symbol: string): UseQueryResult<CompanyEvents> =>
  useQuery(companyEventsQueryOptions(symbol))

/** The page after `last`, or undefined once the list is exhausted. */
const nextPage = (last: { page: number; totalPages: number }): number | undefined =>
  last.page < last.totalPages ? last.page + 1 : undefined

/** One numbered page of AGMs. */
export const companyAgmsQueryOptions = (symbol: string, page: number, pageSize: number) =>
  queryOptions<AgmPage>({
    queryKey: companyKeys.agms(symbol, page, pageSize),
    queryFn: () => companyProfileService.agms(symbol, page, pageSize),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyAgms = (
  symbol: string,
  page: number,
  pageSize: number,
): UseQueryResult<AgmPage> =>
  useQuery({
    ...companyAgmsQueryOptions(symbol, page, pageSize),
    placeholderData: keepPreviousData,
  })

/**
 * AGM history as a load-more feed: page one first, `fetchNextPage` for the
 * next, `hasNextPage` false once `totalPages` is reached. "Showing N of M"
 * is the loaded rows over `pages[0].total`.
 */
export const companyAgmsFeedQueryOptions = (symbol: string, pageSize: number) =>
  infiniteQueryOptions({
    queryKey: companyKeys.agmsFeed(symbol, pageSize),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => companyProfileService.agms(symbol, pageParam, pageSize),
    getNextPageParam: nextPage,
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyAgmsFeed = (
  symbol: string,
  pageSize: number,
): UseInfiniteQueryResult<InfiniteData<AgmPage>> =>
  useInfiniteQuery(companyAgmsFeedQueryOptions(symbol, pageSize))

/** One numbered page of notices — Overview's "News about {symbol}" asks for one short page. */
export const companyAnnouncementsQueryOptions = (symbol: string, page: number, pageSize: number) =>
  queryOptions<AnnouncementPage>({
    queryKey: companyKeys.announcements(symbol, page, pageSize),
    queryFn: () => companyProfileService.announcements(symbol, page, pageSize),
    staleTime: NOTICE_STALE_MS,
  })

export const useCompanyAnnouncements = (
  symbol: string,
  page: number,
  pageSize: number,
): UseQueryResult<AnnouncementPage> =>
  useQuery({
    ...companyAnnouncementsQueryOptions(symbol, page, pageSize),
    placeholderData: keepPreviousData,
  })

/** The Announcements tab as a load-more feed — the same shape as the AGM feed. */
export const companyAnnouncementsFeedQueryOptions = (symbol: string, pageSize: number) =>
  infiniteQueryOptions({
    queryKey: companyKeys.announcementsFeed(symbol, pageSize),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => companyProfileService.announcements(symbol, pageParam, pageSize),
    getNextPageParam: nextPage,
    staleTime: NOTICE_STALE_MS,
  })

export const useCompanyAnnouncementsFeed = (
  symbol: string,
  pageSize: number,
): UseInfiniteQueryResult<InfiniteData<AnnouncementPage>> =>
  useInfiniteQuery(companyAnnouncementsFeedQueryOptions(symbol, pageSize))

// ── Ownership and records ────────────────────────────────────────────────────

/** The promoter/public split, promoters and major shareholders. */
export const companyShareholdingQueryOptions = (symbol: string) =>
  queryOptions<CompanyShareholding>({
    queryKey: companyKeys.shareholding(symbol),
    queryFn: () => companyProfileService.shareholding(symbol),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyShareholding = (symbol: string): UseQueryResult<CompanyShareholding> =>
  useQuery(companyShareholdingQueryOptions(symbol))

/** The board of directors, chair first. */
export const companyBoardQueryOptions = (symbol: string) =>
  queryOptions<BoardMember[]>({
    queryKey: companyKeys.boardOfDirectors(symbol),
    queryFn: () => companyProfileService.boardOfDirectors(symbol),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyBoard = (symbol: string): UseQueryResult<BoardMember[]> =>
  useQuery(companyBoardQueryOptions(symbol))

/** The SWOT notes. `null`: no SWOT on record. */
export const companySwotQueryOptions = (symbol: string) =>
  queryOptions<CompanySwot | null>({
    queryKey: companyKeys.swot(symbol),
    queryFn: () => companyProfileService.swot(symbol),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanySwot = (symbol: string): UseQueryResult<CompanySwot | null> =>
  useQuery(companySwotQueryOptions(symbol))

/** Annual and quarterly report links. */
export const companyAnnualReportsQueryOptions = (symbol: string) =>
  queryOptions<ReportLink[]>({
    queryKey: companyKeys.annualReports(symbol),
    queryFn: () => companyProfileService.annualReports(symbol),
    staleTime: RECORD_STALE_MS,
  })

export const useCompanyAnnualReports = (symbol: string): UseQueryResult<ReportLink[]> =>
  useQuery(companyAnnualReportsQueryOptions(symbol))

// ── Compare ──────────────────────────────────────────────────────────────────

/**
 * Two or three companies side by side.
 *
 * `symbols` is upper-cased and de-duplicated before it becomes the key and the
 * request, but NOT sorted: its order is the column order, and mm-be answers
 * in the order asked.
 */
export const companyCompareQueryOptions = (symbols: readonly string[]) => {
  const normalised = [...new Set(symbols.map((symbol) => symbol.trim().toUpperCase()))]
  return queryOptions<CompanyComparison>({
    queryKey: companyKeys.compare(normalised),
    queryFn: () => companyProfileService.compare(normalised),
    staleTime: QUOTE_STALE_MS,
  })
}

/** The endpoint takes two or three distinct symbols; any other count is a 400. */
const COMPARE_MIN = 2
const COMPARE_MAX = 3

/**
 * Silent until two companies are picked (and past three): a request with any
 * other count is a guaranteed 400. Never pre-pick a peer to fill a slot.
 */
export const useCompanyCompare = (
  symbols: readonly string[],
): UseQueryResult<CompanyComparison> => {
  const distinct = new Set(symbols.map((symbol) => symbol.trim().toUpperCase())).size
  return useQuery({
    ...companyCompareQueryOptions(symbols),
    enabled: distinct >= COMPARE_MIN && distinct <= COMPARE_MAX,
    placeholderData: keepPreviousData,
  })
}

// ── Mitra's take ─────────────────────────────────────────────────────────────

/**
 * One page's take. `null`: the company has no takes at all (a fund, a
 * debenture, an index, or an unknown symbol) — hide the take. Any other
 * failure is an error, retried like every read.
 */
export const companyInsightQueryOptions = (symbol: string, page: InsightPage) =>
  queryOptions<CompanyInsight | null>({
    queryKey: companyKeys.insight(symbol, page),
    queryFn: () => companyInsightsService.insight(symbol, page),
    // Rewritten by the 17:05 after-close run and the :15/:45 edit runs; 5 min
    // shows a run soon after.
    staleTime: READING_STALE_MS,
  })

/**
 * No `keepPreviousData`, unlike the keyed reads above: a take is about ONE
 * page, and a page change must never show the last tab's take under the next.
 */
export const useCompanyInsight = (
  symbol: string,
  page: InsightPage,
): UseQueryResult<CompanyInsight | null> => useQuery(companyInsightQueryOptions(symbol, page))

/**
 * The same take with its explainer, for the "Mitra explains" popup. The
 * card's tier and the card's `null`; a `ready` take's `explainer` is null
 * when mm-be had no titled text for it.
 */
export const companyInsightFullQueryOptions = (symbol: string, page: InsightPage) =>
  queryOptions<CompanyInsight | null>({
    queryKey: companyKeys.insightFull(symbol, page),
    queryFn: () => companyInsightsService.insightFull(symbol, page),
    staleTime: READING_STALE_MS,
  })

/**
 * `enabled` is the popup's open state, so a page never fetches text nobody
 * opened. No `keepPreviousData`, for the reason `useCompanyInsight` gives.
 *
 * When the full read lands it is also written into the card read's cache as
 * the same take with `explainer: null`, so the card under the popup and the
 * popup always show the same run and status: a take the popup found
 * `updating` stops showing as ready (and stops taking votes that 409), and a
 * run written after the card was read replaces the card's old headline. It
 * lives here, beside the two reads' options, because this is the one place
 * that knows both keys; a `select` would not do, as it cannot write a cache.
 */
export const useCompanyInsightFull = (
  symbol: string,
  page: InsightPage,
  { enabled }: { enabled: boolean },
): UseQueryResult<CompanyInsight | null> => {
  const queryClient = useQueryClient()
  const result = useQuery({ ...companyInsightFullQueryOptions(symbol, page), enabled })
  const { data } = result

  useEffect(() => {
    if (data === undefined || data === null) return
    // Only a `ready` take carries an explainer; the others are already card-shaped.
    const cardShaped: CompanyInsight = data.status === 'ready' ? { ...data, explainer: null } : data
    queryClient.setQueryData<CompanyInsight | null>(companyKeys.insight(symbol, page), cardShaped)
  }, [data, queryClient, symbol, page])

  return result
}
